import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  IconButton,
  TextField,
  Drawer,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

// Assets
import facebook from "../Images/facebook.png";
import instagram from "../Images/instagram.png";
import CloseImg from "../Images/CloseImg.png";
import waze from "../Images/waze.png";
import whatsapp from "../Images/whatsapp.png"; // Add WhatsApp icon

// Context
import { useAppContext } from "../AppContext";
import { MyDomain } from "../App";

// ============================================================================
// CONSTANTS
// ============================================================================

const BREAKPOINT_MOBILE = 1024;
const PHONE_PREFIX = "+972";
const MAX_PHONE_LENGTH = 9;

const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/profile.php?id=61576583160431",
  instagram: "https://www.instagram.com/lemaq.store/",
};

const LOCATION = {
  latitude: 32.49285,
  longitude: 35.08549,
  wazeUrl: "waze://?ll=32.49285,35.08549&navigate=yes",
};

const LOCATIONS = {
  latitude: 32.49285,
  longitude: 35.08549,
  wazeUrl:
    "waze://?q=עומר בן אלח'טאב, עארה-ערערה&ll=32.49285,35.08549&navigate=yes",
};

const LANGUAGES = [
  { code: "he", label: "עברית" },
  { code: "ar", label: "العربية" },
  { code: "en", label: "English" },
];

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width:
      window.innerWidth <= 1440
        ? "50%"
        : window.innerWidth <= 1920
        ? "30%"
        : window.innerWidth <= 2560
        ? "50%"
        : window.innerWidth <= 3840
        ? "60%"
        : "50%",
    margin: "0 auto",
    maxWidth: "none",
    borderRadius: 0,
  },
}));

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const validateEmail = (email) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

const formatPhoneNumber = (value) => {
  if (!value.startsWith(PHONE_PREFIX)) {
    value = PHONE_PREFIX;
  }

  let phoneDigits = value.slice(PHONE_PREFIX.length).replace(/\s+/g, "");

  if (phoneDigits.length > MAX_PHONE_LENGTH) {
    phoneDigits = phoneDigits.slice(0, MAX_PHONE_LENGTH);
  }

  if (phoneDigits.length > 2) {
    phoneDigits = phoneDigits.slice(0, 2) + " " + phoneDigits.slice(2);
  }

  return {
    display: PHONE_PREFIX + " " + phoneDigits,
    storage: PHONE_PREFIX + phoneDigits.replace(/\s+/g, ""),
  };
};

const openWaze = () => {
  window.location.href = "https://waze.com/ul/hsvbby6yh6";
};

const openWhatsApp = () => {
  window.location.href = "https://wa.me/972527477128";
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BottomBlock = () => {
  const { Lang, setLang } = useAppContext();
  const { t, i18n } = useTranslation();
  const history = useHistory();

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [hoveredLinkId, setHoveredLinkId] = useState(null);

  const [modals, setModals] = useState({
    callback: false,
    callbackSuccess: false,
    emailSuccess: false,
  });

  const [callbackForm, setCallbackForm] = useState({
    firstName: "",
    phoneNumber: "",
    message: "",
  });

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);

  const isMobile = windowWidth <= BREAKPOINT_MOBILE;

  // ========================================
  // EFFECTS
  // ========================================

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ========================================
  // HANDLERS
  // ========================================

  const openModal = (modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: true }));
  };

  const closeModal = (modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: false }));
  };

  const navigateTo = (path) => {
    history.push(`/${path}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setCallbackForm((prev) => ({ ...prev, phoneNumber: formatted.display }));
  };

  const handlePhoneFocus = () => {
    if (!callbackForm.phoneNumber.startsWith(PHONE_PREFIX)) {
      setCallbackForm((prev) => ({ ...prev, phoneNumber: PHONE_PREFIX }));
    }
  };

  const changeLanguage = (lang) => {
    setLang(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", JSON.stringify(lang));
  };

  // ========================================
  // API CALLS
  // ========================================

  const submitCallback = async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    const data = {
      UserId: user?.userId,
      FullName: callbackForm.firstName,
      PhoneNumber: callbackForm.phoneNumber,
      Text: callbackForm.message,
      IsRead: 0,
    };

    try {
      await axios.post(`${MyDomain}CallBack/CreateCallBack`, data);
      closeModal("callback");
      openModal("callbackSuccess");
      setCallbackForm({ firstName: "", phoneNumber: "", message: "" });
    } catch (error) {
      console.error("Callback submission error:", error.response?.data);
    }
  };

  const submitEmailSubscription = async () => {
    if (!validateEmail(email)) {
      setEmailError(true);
      return;
    }

    setEmailError(false);

    try {
      await axios.post(`${MyDomain}EmailsSubs/CreateEmailsSubs`, {
        Email: email,
      });
      openModal("emailSuccess");
    } catch (error) {
      console.error("Email subscription error:", error.response?.data);
    }
  };

  // ========================================
  // LINK CONFIGURATIONS
  // ========================================

  const footerLinkColumns = [
    {
      id: "column1",
      links: [
        {
          id: 1,
          label: t("navigation.Aboutus"),
          onClick: () => navigateTo("about-us"),
        },
        {
          id: 2,
          label: t("profile.Contactus"),
          onClick: () => navigateTo("feedback"),
        },
        { id: 3, label: t("profile.Termsofuse") },
        { id: 4, label: t("profile.Privacy") },
      ],
    },
    {
      id: "column2",
      links: [
        {
          id: 6,
          label: t("profile.Shipping"),
          onClick: () => navigateTo("delivery"),
        },
        { id: 7, label: t("profile.Aboutourproducts") },
        { id: 8, label: t("profile.ReturnsandExchanges") },
      ],
    },
    {
      id: "column3",
      links: [
        {
          id: 9,
          label: t("profile.Articles"),
          onClick: () => navigateTo("articles"),
        },
        {
          id: 10,
          label: t("profile.News"),
          onClick: () => navigateTo("news"),
        },
        {
          id: 11,
          label: "Beauty Club",
          onClick: () => navigateTo("beauty-club"),
        },
      ],
    },
  ];

  // ========================================
  // RENDER FUNCTIONS
  // ========================================

  const renderCallbackFormContent = () => (
    <div
      style={{
        width: "100%",
        minHeight: "calc(60vh - 55px)",
        padding: 20,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <TextField
        label={t("profile.Fullname")}
        variant="standard"
        fullWidth
        value={callbackForm.firstName}
        onChange={(e) =>
          setCallbackForm((prev) => ({ ...prev, firstName: e.target.value }))
        }
        InputLabelProps={{ style: { opacity: 0.5, fontSize: "15px" } }}
        style={{ paddingBottom: 20 }}
      />

      <TextField
        label={t("profile.Phone")}
        variant="standard"
        fullWidth
        value={callbackForm.phoneNumber}
        onChange={handlePhoneChange}
        onFocus={handlePhoneFocus}
        inputProps={{ maxLength: 14 }}
        InputLabelProps={{ style: { opacity: 0.5, fontSize: "15px" } }}
        style={{ paddingBottom: 35 }}
      />

      <TextField
        label={t("profile.Messagetext")}
        variant="standard"
        fullWidth
        multiline
        rows={4}
        value={callbackForm.message}
        onChange={(e) =>
          setCallbackForm((prev) => ({ ...prev, message: e.target.value }))
        }
        InputLabelProps={{ style: { opacity: 0.5, fontSize: "15px" } }}
        style={{ paddingBottom: 20 }}
      />

      <button
        onClick={submitCallback}
        style={{
          marginTop: 20,
          backgroundColor: "#27272a",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          fontSize: "16px",
          width: "100%",
          height: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: 14 }}>{t("profile.Send")}</span>
      </button>
    </div>
  );

  const renderCallbackModal = () => {
    if (isMobile) {
      return (
        <Drawer
          open={modals.callback}
          onClose={() => closeModal("callback")}
          anchor="bottom"
          PaperProps={{
            style: {
              height: "auto",
              overflow: "auto",
            },
          }}
        >
          <div
            style={{
              display: "flex",
              paddingBottom: 20,
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: "2%",
              paddingLeft: "2%",
              paddingTop: 15,
              direction: i18n.resolvedLanguage === "en" ? "ltr" : "rtl",
            }}
          >
            <span style={{ fontSize: 20 }}>
              {t("profile.WEWILLCALLYOUBACK")}
            </span>
            <img
              onClick={() => closeModal("callback")}
              src={CloseImg}
              alt="Close"
              style={{ width: 25, height: 25, cursor: "pointer" }}
            />
          </div>
          {renderCallbackFormContent()}
        </Drawer>
      );
    }

    return (
      <BootstrapDialog
        open={modals.callback}
        onClose={() => closeModal("callback")}
        fullWidth
        maxWidth={false}
      >
        <DialogTitle>{t("profile.WEWILLCALLYOUBACK")}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => closeModal("callback")}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {renderCallbackFormContent()}
      </BootstrapDialog>
    );
  };

  const renderConfirmationModal = (modalName, message, buttonText = "OK") => (
    <BootstrapDialog
      open={modals[modalName]}
      onClose={() => closeModal(modalName)}
      fullWidth
      maxWidth={false}
    >
      {modalName === "emailSuccess" && (
        <IconButton
          aria-label="close"
          onClick={() => closeModal(modalName)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <div
        style={{
          width: "100%",
          minHeight: buttonText ? "calc(50vh - 55px)" : "calc(15vh - 55px)",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p style={{ textAlign: "center", margin: 0 }}>{message}</p>

        {buttonText && (
          <button
            onClick={() => closeModal(modalName)}
            style={{
              marginTop: 40,
              backgroundColor: "#27272a",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontSize: "16px",
              width: "50%",
              height: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: 14 }}>{buttonText}</span>
          </button>
        )}
      </div>
    </BootstrapDialog>
  );

  const renderSocialIcon = (icon, alt, url, marginRight = 0) => (
    <div
      onClick={() => window.open(url, "_blank")}
      style={{
        border: "1px solid #e4e4e7",
        padding: isMobile ? 10 : 15,
        borderRadius: "50%",
        display: "flex",
        marginRight,
        cursor: "pointer",
      }}
    >
      <img
        src={icon}
        alt={alt}
        style={{
          minWidth: isMobile ? "15px" : "20px",
          height: isMobile ? "15px" : "20px",
        }}
      />
    </div>
  );

  const renderSubscriptionSection = () => (
    <div
      style={{
        height: 400,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#fafafa",
        flexDirection: "column",
      }}
    >
      <p
        style={{
          fontSize: isMobile ? 15 : 25,
          fontWeight: 500,
          margin: 0,
          marginBottom: 20,
        }}
      >
        {t("profile.Bethefirsttoknowaboutsalesandnewarrivals")}
      </p>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          width: isMobile ? "80%" : "25%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <TextField
          label={t("profile.email")}
          variant="standard"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          InputLabelProps={{
            style: { color: "#a1a1aa", fontSize: "14px" },
          }}
          sx={{
            paddingBottom: "30px",
            fontSize: "10px",
            "& .MuiInput-underline:before": {
              borderBottomColor: "#a1a1aa",
              opacity: 0.5,
              borderBottomWidth: "1px",
            },
            "& .MuiInput-underline:hover:before": {
              borderBottomColor: "#a1a1aa",
              opacity: 0.8,
              borderBottomWidth: "1px",
            },
            "& .MuiInput-underline:after": {
              borderBottomColor: "#6366f1",
              borderBottomWidth: "1px",
            },
            "& .MuiInputBase-root": {
              fontSize: "14px",
            },
          }}
        />

        <button
          onClick={submitEmailSubscription}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f4f4f5",
            border: "none",
            cursor: "pointer",
            marginBottom: 10,
            padding: "8px 16px",
          }}
        >
          <span style={{ fontSize: 15, margin: 0 }}>
            {t("profile.subscribe")}
          </span>
        </button>
      </div>

      {emailError && (
        <div
          style={{ display: "flex", width: "100%", justifyContent: "center" }}
        >
          <span style={{ fontSize: 14, color: "red" }}>
            Please check your email
          </span>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "row", marginTop: 20 }}>
        {renderSocialIcon(facebook, "Facebook", SOCIAL_LINKS.facebook, 20)}
        {renderSocialIcon(instagram, "Instagram", SOCIAL_LINKS.instagram)}
      </div>
    </div>
  );

  const renderLanguageSelector = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        paddingTop: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingRight: 20,
        }}
      >
        {LANGUAGES.map(({ code, label }) => (
          <p
            key={code}
            onClick={() => changeLanguage(code)}
            style={{
              margin: 5,
              padding: 0,
              fontSize: 14,
              color: Lang === code ? "black" : "#a1a1aa",
              fontWeight: Lang === code ? "bold" : "normal",
              cursor: "pointer",
            }}
          >
            {label}
          </p>
        ))}
      </div>
    </div>
  );

  const renderContactSection = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: isMobile ? "100%" : "auto",
        justifyContent: isMobile ? "center" : "flex-start",
        alignItems: isMobile ? "center" : "flex-start",
      }}
    >
      <p
        style={{
          paddingBottom: 15,
          fontWeight: "600",
          fontSize: 20,
          margin: 0,
        }}
      >
        {i18n.resolvedLanguage === "en"
          ? "+972 52-747-7128"
          : "52-747-7128 972+"}
      </p>

      {!isMobile && (
        <p
          style={{ fontSize: 14, paddingTop: 10, paddingBottom: 5, margin: 0 }}
        >
          <span style={{ opacity: 0.5 }}>{t("sentence.Youcan")} </span>
          <span
            onClick={() => navigateTo("feedback")}
            style={{
              color: "#6b21a8",
              cursor: "pointer",
            }}
          >
            {t("sentence.emailus")}{" "}
          </span>
          <span style={{ opacity: 0.5, fontSize: 14 }}>
            {t("sentence.callus")}
          </span>
        </p>
      )}

      <p style={{ opacity: 0.5, fontSize: 14, margin: 0 }}>
        {t("profile.SunFri")}
      </p>

      {/* WhatsApp Button */}
      <button
        onClick={openWhatsApp}
        style={{
          marginTop: 15,
          backgroundColor: "#25D366",
          color: "white",
          padding: "12px 16px",
          borderRadius: "8px",
          border: "none",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          transition: "background-color 0.3s",
          width: isMobile ? "100%" : "auto",
        }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = "#20BA5A")}
        onMouseLeave={(e) => (e.target.style.backgroundColor = "#25D366")}
      >
        <img
          src={whatsapp}
          alt="WhatsApp"
          style={{
            width: 20,
            height: 20,
          }}
        />
        פתח ב-WhatsApp
      </button>

      {/* Waze Button */}
      <button
        onClick={openWaze}
        style={{
          marginTop: 10,
          backgroundColor: "#34C759",
          color: "white",
          padding: "12px 16px",
          borderRadius: "8px",
          border: "none",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          transition: "background-color 0.3s",
          width: isMobile ? "100%" : "auto",
        }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = "#2FB84D")}
        onMouseLeave={(e) => (e.target.style.backgroundColor = "#34C759")}
      >
        <img
          src={waze}
          alt="Waze"
          style={{
            width: 20,
            height: 20,
          }}
        />
        {t("profileOrderHistory.Place") || "פתח ב-Waze"}
      </button>

      <p
        onClick={() => openModal("callback")}
        style={{
          paddingTop: 20,
          fontSize: 14,
          color: "#6b21a8",
          cursor: "pointer",
          margin: 0,
        }}
      >
        {t("profile.Callback")}
      </p>

      {!isMobile && renderLanguageSelector()}
    </div>
  );

  const renderFooterLinks = () => (
    <div
      style={{
        flexDirection: "row",
        display: "flex",
        justifyContent: "space-between",
        paddingTop: isMobile ? 30 : 100,
      }}
    >
      {footerLinkColumns.map((column) => (
        <div
          key={column.id}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {column.links.map(({ id, label, onClick }) => (
            <p
              key={id}
              onMouseEnter={() => setHoveredLinkId(id)}
              onMouseLeave={() => setHoveredLinkId(null)}
              onClick={onClick}
              style={{
                fontSize: 14,
                paddingBottom: 10,
                cursor: "pointer",
                color: hoveredLinkId === id ? "#6b21a8" : "black",
                margin: 0,
              }}
            >
              {label}
            </p>
          ))}
        </div>
      ))}

      {!isMobile && renderContactSection()}
    </div>
  );

  // ========================================
  // MAIN RENDER
  // ========================================

  return (
    <footer
      style={{
        paddingTop: 100,
        paddingLeft: isMobile ? "2%" : "0%",
        paddingRight: isMobile ? "2%" : "0%",
        direction: i18n.resolvedLanguage === "en" ? "ltr" : "rtl",
      }}
    >
      {/* Modals */}
      {renderCallbackModal()}
      {renderConfirmationModal(
        "callbackSuccess",
        t("profile.Greatwewillcontactyouassoonaspossible"),
        t("profile.Done")
      )}
      {renderConfirmationModal(
        "emailSuccess",
        "Great, you will be first who receive newsletter!",
        null
      )}

      {/* Subscription Section */}
      {renderSubscriptionSection()}

      {/* Mobile Contact Section */}
      {isMobile && (
        <div style={{ display: "flex", width: "100%" }}>
          {renderContactSection()}
        </div>
      )}

      {/* Footer Links */}
      {renderFooterLinks()}

      {/* Divider */}
      <div
        style={{ height: "0.1px", backgroundColor: "#f4f4f5", width: "100%" }}
      />

      {/* Copyright */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingTop: 100,
          flexDirection: "column",
          paddingBottom: 60,
        }}
      >
        <p style={{ fontSize: 14, color: "#6b21a8", fontWeight: 500 }}>
          LEMAQ™. BEAUTY WITHOUT LIMITS
        </p>
        <p style={{ fontSize: 12 }}>© LEMAQ 2024-2025</p>
      </div>
    </footer>
  );
};

export default BottomBlock;
